import { notification } from "antd";
import Helper from "../../Helper";
import { globalAppConfig } from "../../config";
import { Team } from "../../models/Team";
import TeamInfo from "../../teamInfo";

export function searchTeams(search: string): Promise<Team[]> {
    const url = "/teams?nameFilter=" + search;

    // TODO: Should we also have the backend fully block this?
    // Just go ahead and block trying to search for every character, every string, to get every team
    if (search.length < 3) {
        return Promise.resolve([]);
    }

    return Helper.plotFetch(url, {}, true)
        .then((response) => response.json())
        .then((response) => {
            // We don't care about a timezone here, and we don't have a specific project so we don't really know
            // So just use the user's browser timezone
            return response.map((team) => Team.fromApiObject(team, Intl.DateTimeFormat().resolvedOptions().timeZone));
        })
        .catch(() => {
            return [];
        });
}

export function getTeamById(id: number): Promise<object> {
    const url = `${Helper.baseUrlWithPort()}/teams/${id}`;
    return Helper.plotFetch(url, {}, true)
        .then((response) => response.json())
        .then((response) => {
            if (response.length === 0) {
                return null;
            }
            return response;
        })
        .catch(() => {
            return null;
        });
}

export function getTeamAvatarUrl(teamId: number): Promise<string | null> {
    const url = `${Helper.baseUrlWithPort()}/teams/${teamId}`;

    return Helper.plotFetch(url, {}, true)
        .then((response) => response.json())
        .then((response) => {
            if (response.length === 0) {
                return null;
            }
            return response.avatar_url;
        })
        .catch(() => {
            return null;
        });
}

/**
 * Get the list of projects that the team is involved with (owned and joined)
 * @returns Object of arrays of projects and joined projects for the current team, or null if no signed in
 *  response {
 *      projects: [
 *          { id: 1, project_name: "example1", ... },
 *          { id: 2, project_name: "example2", ... }
 *      ],
 *      joined_project_links: [
 *          { id: 3, project_name: "example3", ...},
 *          { id: 4, project_name: "example4", ...}
 *      ]
 *  }
 */
export function getProjectsOnTeam(subdomain: string): Promise<{
    member_of: any[];
    not_member_of: any[];
    projects: any[];
    recent_projects: any[];
    vendor_for: any[];
} | null> {
    const url = Helper.baseUrlAltSubdomain(subdomain) + "/projects";

    return Helper.plotFetch(url, {}, true)
        .then((response) => response.json())
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
}

/**
 * Get the current subdomains team
 * @returns Team Info or null if failed to get
 */
export function getCurrentSubdomainTeam(): Promise<object> {
    const url = "/teams/current";
    return Helper.plotFetch(url, {}, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to get team info");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
}

/**
 * Gets the current users access level to the current team based on subdomain
 * @returns Access level info or null
 */
export function getCurrentSubdomainTeamAccessLevel(): Promise<{
    level: number;
    message: string;
}> {
    const url = "/teams/current/access_level";
    return Helper.plotFetch(url, {}, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to get team info");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
}

/**
 * Create a new team using the given info
 * @param teamInfo Info to fill in the team for team params
 * @returns Newly created team info, or null if error
 */
export function postTeam(teamInfo, addCurrentUserToTeam: boolean = false): Promise<any | null> {
    const url = "/teams";
    const options = {
        method: "post",
        body: JSON.stringify({ team: teamInfo, add_current_user_to_team: addCurrentUserToTeam ? "true" : null }),
    };
    return Helper.plotFetch(url, options)
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else if (response.status == 422) {
                return response.json();
            }

            throw new Error("Failed to create team");
        })
        .then((response) => {
            if (response.error) {
                return null;
            }
            return response;
        })
        .catch(() => {
            return null;
        });
}

export function putTeamUpdateMembers(teamInfo: TeamInfo): Promise<object> {
    const url = "/teams/update_members";
    const options = {
        method: "put",
        body: JSON.stringify({ users: teamInfo.users }),
    };
    return Helper.plotFetch(url, options)
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else if (response.status == 422) {
                return response.json();
            }

            throw new Error("Failed to update team");
        })
        .then((response) => {
            if (response.error) {
                return null;
            }
            return response;
        })
        .catch(() => {
            return null;
        });
}

export function putTeam(team_id: number, teamInfo: any): Promise<object> {
    const url = "/teams/" + team_id;
    const options = {
        method: "put",
        body: JSON.stringify({ team: teamInfo }),
    };
    return Helper.plotFetch(url, options)
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else if (response.status == 422) {
                return response.json();
            }

            throw new Error("Failed to update team");
        })
        .then((response) => {
            if (response.error) {
                return null;
            }
            return response;
        })
        .catch(() => {
            return null;
        });
}

export function requestToJoinTeam(team_id: number): Promise<any> {
    const url = "/teams/request_to_join_team/" + team_id;
    const options = {
        method: "post",
    };
    return Helper.plotFetch(url, options, true)
        .then((response) => {
            let message, description;
            switch (response.message) {
                case "RequestAutoApproved":
                    message = "The team has auto approved your join request";
                    description = "You match a rule for auto approval. Automatically adding you to the team";
                    break;
                case "RequestAlreadySent":
                    message = "Request already sent to team";
                    description = "You have already requested to join this team. Sending a reminder to the team admin";
                    break;
                default: //Default is "RequestSent"
                    message = "Your request to join has been sent";
                    description = "The team has received your request";
            }
            notification.success({
                message: message,
                description: description,
                duration: globalAppConfig.NOTIFICATION_DURATION,
            });
            return response;
        })
        .catch(() => {
            notification.error({
                message: "Your request to join failed to be sent",
                description: "Failure to send request",
                duration: globalAppConfig.NOTIFICATION_DURATION,
            });
        });
}

export function getUniqueSubdomainForTeam(suggestion: string): Promise<string> {
    const url = Helper.baseUrlAltSubdomain("teams") + `/subdomains/suggest?company_name=${suggestion}`;
    return Helper.plotFetch(url)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Failed to get unique team subdomain");
        })
        .then((response) => {
            return response.suggestion;
        })
        .catch(() => {
            return null;
        });
}

export function shareTeam(subdomain: string, contacts: { contact_info: string }[]): Promise<any> {
    const url = Helper.baseUrlAltSubdomain(subdomain) + "/teams/share";
    const options = {
        method: "put",
        body: JSON.stringify({ contacts: contacts }),
    };
    return Helper.plotFetch(url, options, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw new Error("Failed to share company");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
}

export function inviteToTeam(
    contacts: { contact_info: string; permission: "is_subscribed" | "is_member" | "is_lead" }[],
    subdomain: string,
    invited_project_token?: string
): Promise<any> {
    let url = "/teams/invite";
    contacts = contacts.map((contact) => {
        contact.contact_info = contact.contact_info.trim();
        return contact;
    });

    if (subdomain && subdomain.length > 0) {
        url = Helper.baseUrlAltSubdomain(subdomain) + "/teams/invite";
    }
    const options = {
        method: "post",
        body: JSON.stringify({
            contacts: contacts,
            project_unique_token: invited_project_token,
        }),
    };
    return Helper.plotFetch(url, options, true)
        .then((data) => {
            if (data.ok) {
                return data.json();
            }
            throw new Error("Network error.");
        })
        .then((data) => {
            return data;
        })
        .catch(() => {
            return null;
        });
}

export function manageActiveInactiveProjects(active_projects: any[], inactive_projects: any[]): Promise<boolean> {
    const url = "/teams/manage_active_inactive";
    const options = {
        method: "post",
        body: JSON.stringify({
            active_projects: active_projects,
            inactive_projects: inactive_projects,
        }),
    };
    return Helper.plotFetch(url, options, true)
        .then((data) => {
            if (data.ok) {
                return data.json();
            }
            throw new Error("Network error.");
        })
        .then(() => {
            return true;
        })
        .catch(() => {
            return false;
        });
}

export async function getTeamPicBase64(team: TeamInfo): Promise<string | null> {
    return Helper.plotFetch(team.avatarUrl)
        .then((response) => response.blob())
        .then(
            (blob) =>
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = function () {
                        resolve(reader.result);
                    };
                    reader.readAsDataURL(blob);
                })
        );
}

export function uploadTeamPic(team: TeamInfo, imageAsDataURL: string): Promise<boolean> {
    const url = `${Helper.baseUrlAltSubdomain(team.subdomain)}/teams/picture`;
    return Helper.plotFetch(url, {
        method: "POST",
        body: JSON.stringify({ picture: imageAsDataURL }),
    })
        .then((response) => {
            if (response.ok) {
                return true;
            }
            return false;
        })
        .catch(() => {
            return false;
        });
}

/**
 * Deletes the team's current profile picture
 * @returns boolean indicating success in removal
 */

export function deleteTeamPic(team: TeamInfo): Promise<boolean> {
    const url = `${Helper.baseUrlAltSubdomain(team.subdomain)}/teams/${team.id}/picture`;
    return Helper.plotFetch(url, {
        method: "DELETE",
        credentials: "include",
    })
        .then((response) => {
            if (response.ok) {
                return true;
            }
            return false;
        })
        .catch(() => {
            return false;
        });
}

export function ssoConfig(team_subdomain: string): Promise<any | null> {
    const url = `${Helper.baseUrlAltSubdomain(team_subdomain)}/teams/sso_config`;

    return Helper.plotFetch(url, {
        method: "GET",
        credentials: "include",
    })
        .then((response) => response.json())
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
}
